export interface QuestionnaireTreeQuestion {
  id: number;
  type: string | null;
  options: Array<{ value: string }> | null;
  depth: number | null;
  parent_question_id: number | null;
}

export interface QuestionnaireTreeAnswer {
  id: number;
  question: QuestionnaireTreeQuestion | null;
  answers: Array<{ value: string | number }> | null;
}

/**
 * Builds Q-numbering (Q1, Q1.1, Q1.1.1…) from each answer's question depth/
 * parent_question_id. The server (No.206) already returns answers in tree
 * order — this only labels them, it does not re-sort.
 */
export function buildQuestionLabels(answers: QuestionnaireTreeAnswer[]): Record<string, string> {
  const labels: Record<string, string> = {};
  const parentSubCounts: Record<string, number> = {};
  let rootCount = 0;

  (answers || []).forEach((ans) => {
    const q = ans.question;
    if (!q) {
      return;
    }
    const qId = String(q.id);
    const depth = q.depth || 0;

    if (depth === 0) {
      rootCount++;
      labels[String(ans.id)] = `${rootCount}`;
      labels[`q_${qId}`] = `${rootCount}`;
    } else {
      const parentId = String(q.parent_question_id);
      const parentLabel = labels[`q_${parentId}`] || '?';
      if (!parentSubCounts[parentId]) {
        parentSubCounts[parentId] = 0;
      }
      parentSubCounts[parentId]++;
      const label = `${parentLabel}.${parentSubCounts[parentId]}`;
      labels[String(ans.id)] = label;
      labels[`q_${qId}`] = label;
    }
  });

  return labels;
}

/** The radio/checkbox options the patient did NOT select, shown grayed-out as 他の選択肢. */
export function getOtherSelectableOptions(ans: QuestionnaireTreeAnswer): string[] {
  const options = Array.isArray(ans?.question?.options) ? ans.question!.options! : [];
  const allOptions = options.map((opt) => opt.value);
  const selectedValues = (ans.answers || []).map((a) => a.value);

  return allOptions.filter((opt) => !selectedValues.includes(opt?.trim?.()));
}

export function hasOtherSelectableOptions(ans: QuestionnaireTreeAnswer): boolean {
  const isSelectable = ['checkbox', 'radio'].includes(ans.question?.type || '');
  return isSelectable && getOtherSelectableOptions(ans).length > 0;
}
